import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users2, X, ArrowRight, Loader2, CheckCircle2, DollarSign, Sparkles } from "lucide-react";

const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const strengthFor = (sim) => {
  if (sim >= 0.7) return { label: "STRONG", classes: "bg-emerald-50 border-emerald-200 text-emerald-800" };
  if (sim >= 0.4) return { label: "MEDIUM", classes: "bg-sky-50 border-sky-200 text-sky-800" };
  return { label: "WEAK", classes: "bg-slate-50 border-slate-200 text-slate-700" };
};

/**
 * Rate Card Auto-Match nudge — surfaces services-mode line items whose
 * labor_category doesn't exactly match a workspace rate card entry. One-click
 * Map re-labels the line and swaps in the workspace rate, keeping the pricing
 * consistent with the roster.
 */
export default function RateCardAutoMatchCard({ noticeId, isServices, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState({});
  const [dismissed, setDismissed] = useState({});

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/ai/estimate/${noticeId}/rate-card-suggestions`); setData(r.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isServices) load(); }, [isServices, noticeId]);

  if (!isServices || loading || !data?.available) return null;

  const activeSuggestions = (data.suggestions || []).filter((s) => !dismissed[`${s.ci}-${s.ii}`]);
  if (activeSuggestions.length === 0) return null;

  const apply = async (s) => {
    const key = `${s.ci}-${s.ii}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await api.post(`/ai/estimate/${noticeId}/apply-rate-card`, {
        category_index: s.ci, item_index: s.ii, cat_id: s.suggested.cat_id,
      });
      toast.success(`Mapped to "${r.data.mapped_to}" @ $${r.data.new_rate}/hr`);
      setDismissed((d) => ({ ...d, [key]: true }));
      if (onApplied) onApplied(r.data.estimate);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply mapping");
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const skip = (s) => setDismissed((d) => ({ ...d, [`${s.ci}-${s.ii}`]: true }));

  return (
    <>
      <div className="rounded-2xl border border-indigo-500/50 bg-gradient-to-br from-indigo-950/40 via-[#0D1424] to-fuchsia-950/40 p-4 flex items-start gap-3" data-testid="rate-card-nudge">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center shrink-0">
          <Users2 className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono font-bold tracking-widest text-indigo-300">// RATE CARD AUTO-MATCH</div>
          <div className="font-display font-bold text-lg text-white leading-tight mt-0.5">
            {activeSuggestions.length} labor {activeSuggestions.length === 1 ? "category" : "categories"} don't match your rate card
          </div>
          <div className="text-sm text-slate-300 mt-1">
            {(data.suggestions || []).filter((s) => s.suggested.reason === "rate_mismatch").length > 0 && "Some rows use your role names but with different rates. "}
            Map them to workspace roles and the direct rate + downstream cost-to-gov update automatically.
          </div>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="px-4 py-2 rounded-full text-xs font-mono font-bold tracking-widest text-white bg-gradient-to-br from-indigo-500 to-fuchsia-500 hover:brightness-110 inline-flex items-center gap-1.5 shrink-0"
          data-testid="rate-card-review-btn"
        >
          REVIEW <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rate-card-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono font-bold tracking-widest text-indigo-700">RATE CARD AUTO-MATCH</div>
                <div className="font-display font-bold text-lg text-slate-900">Map AI-picked roles to your rate card</div>
                <div className="text-xs text-slate-500 mt-0.5">Each Map action snaps the line's labor category + direct rate to the workspace roster and re-computes totals.</div>
              </div>
              <button onClick={() => setModalOpen(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-rose-600" data-testid="rate-card-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {activeSuggestions.length === 0 && (
                <div className="text-sm text-slate-500 p-8 text-center" data-testid="rate-card-empty">
                  All mapped — your estimate now matches your rate card.
                </div>
              )}
              {activeSuggestions.map((s) => {
                const key = `${s.ci}-${s.ii}`;
                const strength = strengthFor(s.suggested.similarity);
                const rateMismatch = s.suggested.reason === "rate_mismatch";
                const rateDelta = s.current_rate && s.suggested.default_direct_rate ? s.suggested.default_direct_rate - Number(s.current_rate) : 0;
                return (
                  <div key={key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`rate-card-row-${key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[10px] font-mono font-bold tracking-widest rounded-full border px-2 py-0.5 ${strength.classes}`}>
                        {strength.label} · {(s.suggested.similarity * 100).toFixed(0)}%
                      </span>
                      {rateMismatch && (
                        <span className="text-[10px] font-mono font-bold tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          RATE MISMATCH
                        </span>
                      )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 items-center">
                      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                        <div className="text-[9px] font-mono font-bold tracking-widest text-slate-500 mb-1">CURRENT (AI-PICKED)</div>
                        <div className="font-semibold text-slate-900">{s.current_label}</div>
                        <div className="text-xs text-slate-500 font-mono mt-1">{fmt(s.current_rate)}/hr direct</div>
                      </div>
                      <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
                        <div className="text-[9px] font-mono font-bold tracking-widest text-indigo-700 mb-1">YOUR RATE CARD</div>
                        <div className="font-semibold text-slate-900">{s.suggested.name}</div>
                        <div className="text-xs text-indigo-700 font-mono mt-1">
                          {fmt(s.suggested.default_direct_rate)}/hr direct
                          {rateDelta !== 0 && (
                            <span className={`ml-2 ${rateDelta > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                              ({rateDelta > 0 ? "+" : ""}{fmt(rateDelta)})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                      <button onClick={() => skip(s)} className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800 px-4 py-2" data-testid={`rate-card-skip-${key}`}>
                        SKIP
                      </button>
                      <button
                        onClick={() => apply(s)}
                        disabled={!!busy[key]}
                        className="bf-pill px-4 py-2 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid={`rate-card-apply-${key}`}
                      >
                        {busy[key] ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        MAP
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <span>Skip persists only for this session — reopen to see it again.</span>
              <button onClick={() => setModalOpen(false)} className="text-xs font-mono font-bold tracking-widest text-indigo-700 hover:text-indigo-900" data-testid="rate-card-done">
                DONE
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
